/**
 * Side-channel WebSocket subscription for voice events.
 *
 * Voice-as-chat-channel cutover: the active-context, redaction, and cost
 * surfaces this hook used to feed are now owned elsewhere —
 *   - referenced sources + redaction ride each turn's `GroundingReport` on the
 *     broadcast `ChatMessage` (rendered by the chat thread), and
 *   - the running call meter sums the bound chat session's voice turns.
 * So this hook no longer mutates the voice store. It is retained as the seam
 * for any genuinely call-scoped side-channel signal (e.g. a future structured
 * `voice.cost_tick` push for a live per-minute EL voice-cost ticker) and, in
 * the meantime, is a harmless no-op: it does not subscribe to anything until
 * such an event shape exists.
 *
 * Subscription model (when a consumer is re-added in a later wave):
 *   - Bound to the current sessionId; events for a *different* session id are
 *     ignored.
 *   - `wsManager.onAnyMessage` returns a disposer registered in the effect and
 *     released on cleanup, so re-mounts don't leak handlers.
 */
import { useEffect } from 'react';

/**
 * No-op while there is no call-scoped side-channel consumer. Takes the
 * sessionId so call sites stay stable across waves (Wave 3 re-hosts this hook
 * inside the chat panel). The effect intentionally does nothing today.
 */
export function useVoiceWebSocket(sessionId: string | null): void {
  useEffect(() => {
    // Nothing to subscribe to yet — see the file header. Keeping the effect
    // (even empty) preserves the hook's call-site contract and the sessionId
    // dependency for when a live consumer returns.
    void sessionId;
    return undefined;
  }, [sessionId]);
}
